import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Target, Mail, MessageSquare, BarChart3, Workflow, CheckCircle2 } from "lucide-react";
import aiveliqLogo from "@/assets/aiveliq360-logo.png";
import { SEOHead } from "@/components/seo/SEOHead";

const features = [
  {
    icon: Target,
    title: "Lead Discovery Engine",
    subtitle: "AI Lead Generation",
    description: "Automatically identifies high-quality leads and prioritizes them using predictive scoring.",
  },
  {
    icon: Mail,
    title: "AI Engagement Workflows",
    subtitle: "Sales Automation",
    description: "AI designs, deploys, and optimizes multi-channel outreach.",
  },
  {
    icon: MessageSquare,
    title: "Real-Time Sales Coaching",
    subtitle: "AI Coaching",
    description: "Boost rep productivity with dynamic decision support during calls and outreach.",
  },
  {
    icon: BarChart3,
    title: "Revenue Intelligence",
    subtitle: "Pipeline Forecasting",
    description: "Forecast revenue using real-time behavioral and deal progression data.",
  },
  {
    icon: Workflow,
    title: "Unified GTM Automation",
    subtitle: "Workflow Automation",
    description: "Align your full customer lifecycle with AI-orchestrated flows.",
  },
];

export default function AISalesOS() {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead 
        title="Aiveliq360 AI Sales OS | Unified AI Platform for End-to-End Sales"
        description="Discover the AI Sales Operating System that automates prospecting, engagement, coaching, and forecasting. Aiveliq360 delivers predictable, scalable revenue growth."
      />

      {/* Header */}
      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/">
            <img src={aiveliqLogo} alt="Aiveliq360" className="h-10 object-contain" />
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/pricing">
              <Button variant="ghost" size="sm">Pricing</Button>
            </Link>
            <Link to="/auth">
              <Button size="sm" className="gap-1.5">
                Get Started
                <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative py-16 lg:py-24">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-purple-500/5" />
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-3xl lg:text-5xl font-bold tracking-tight mb-6">
            Aiveliq360: The Autonomous{" "}
            <span className="gradient-text">AI Sales Operating System</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Aiveliq360 consolidates the entire revenue function — from lead generation to forecasting — 
            into one intelligent platform that executes, guides, and optimizes your GTM operations using real-time AI.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/auth">
              <Button size="lg" className="gap-2 ai-glow">
                Start Free Trial
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <Link to="/pricing">
              <Button size="lg" variant="outline">View Pricing</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl lg:text-3xl font-bold text-center mb-12">
            Key Features
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature) => (
              <article key={feature.title} className="glass rounded-xl p-6 hover:border-primary/30 transition-all">
                <div className="p-2 rounded-lg bg-primary/10 w-fit mb-4">
                  <feature.icon className="h-6 w-6 text-primary" />
                </div>
                <span className="text-xs text-primary font-medium">{feature.subtitle}</span>
                <h3 className="text-lg font-semibold mt-1 mb-2">{feature.title}</h3>
                <p className="text-muted-foreground text-sm">{feature.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold mb-4">Ready to Transform Your Sales?</h2>
          <p className="text-muted-foreground mb-8">
            See how Aiveliq360 can automate your entire sales funnel.
          </p>
          <Link to="/auth">
            <Button size="lg" className="gap-2 ai-glow">
              Request a Demo
              <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-6">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground">← Back to Home</Link>
          <p>© 2025 Aiveliq360</p>
        </div>
      </footer>
    </div>
  );
}
